import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, RefreshCw, CheckCircle, XCircle, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Custom hook holding all SuperAdmin dashboard state and handlers.
 * Returned object is shared via SuperAdminContext.
 */
export function useSuperAdminData() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [allInquiries, setAllInquiries] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [peptideOnly, setPeptideOnly] = useState(false);
  const [exportingInquiries, setExportingInquiries] = useState(false);
  const [landingConversion, setLandingConversion] = useState(null);
  const [bankAccount, setBankAccount] = useState(null);
  const [showBankModal, setShowBankModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [bankDetails, setBankDetails] = useState({
    account_holder: '', bank_name: '', account_number: '',
    routing_number: '', account_type: 'checking'
  });
  // Bank Verification
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  // Fee Management
  const [merchantFees, setMerchantFees] = useState([]);
  const [editingFee, setEditingFee] = useState(null);
  const [newFeeValue, setNewFeeValue] = useState('');
  // Payout Management
  const [merchantBalances, setMerchantBalances] = useState([]);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [payoutsSummary, setPayoutsSummary] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [showUpdatePayoutModal, setShowUpdatePayoutModal] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutUpdateStatus, setPayoutUpdateStatus] = useState('');
  const [payoutAdminNotes, setPayoutAdminNotes] = useState('');
  const [payoutFailureReason, setPayoutFailureReason] = useState('');
  // Financial Reports
  const [financialReport, setFinancialReport] = useState(null);
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [reportPeriod, setReportPeriod] = useState('30d');
  const [exportingReport, setExportingReport] = useState(false);
  // Agents
  const [agents, setAgents] = useState([]);
  const [agentBalances, setAgentBalances] = useState([]);
  const [agentCommissions, setAgentCommissions] = useState([]);
  const [agentSummary, setAgentSummary] = useState(null);
  const [showAgentPayoutModal, setShowAgentPayoutModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentPayoutAmount, setAgentPayoutAmount] = useState('');
  const [agentPayoutReference, setAgentPayoutReference] = useState('');
  const [agentPayoutNotes, setAgentPayoutNotes] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [activeTab, reportPeriod, peptideOnly]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const statsRes = await fetch(`${BACKEND_URL}/api/super-admin/platform-stats`, { headers: authHeaders() });
      if (statsRes.ok) setStats(await statsRes.json());

      // Landing conversion metrics (lightweight, always fetch alongside stats)
      try {
        const lc = await fetch(`${BACKEND_URL}/api/super-admin/landing-conversion?days=30`, { headers: authHeaders() });
        if (lc.ok) setLandingConversion(await lc.json());
      } catch (e) {
        console.warn('landing-conversion fetch failed (non-blocking):', e);
      }

      if (activeTab === 'users') {
        const r = await fetch(`${BACKEND_URL}/api/super-admin/all-users?limit=100`, { headers: authHeaders() });
        if (r.ok) setAllUsers((await r.json()).users);
      } else if (activeTab === 'transactions') {
        const r = await fetch(`${BACKEND_URL}/api/super-admin/all-transactions?limit=100`, { headers: authHeaders() });
        if (r.ok) setAllTransactions((await r.json()).transactions);
      } else if (activeTab === 'inquiries') {
        const qs = `limit=100${peptideOnly ? '&peptide_only=true' : ''}`;
        const r = await fetch(`${BACKEND_URL}/api/super-admin/all-inquiries?${qs}`, { headers: authHeaders() });
        if (r.ok) setAllInquiries((await r.json()).inquiries);
      } else if (activeTab === 'automation') {
        const r = await fetch(`${BACKEND_URL}/api/super-admin/scheduler-status`, { headers: authHeaders() });
        if (r.ok) setSchedulerStatus(await r.json());
      } else if (activeTab === 'financial') {
        await fetchBankAccount();
      } else if (activeTab === 'verification') {
        await fetchPendingVerifications();
      } else if (activeTab === 'fees') {
        await fetchMerchantFees();
      } else if (activeTab === 'payouts') {
        await fetchMerchantBalances();
        await fetchPayouts();
      } else if (activeTab === 'reports') {
        await fetchFinancialReport();
        await fetchRevenueChart();
      } else if (activeTab === 'agents') {
        await fetchAgents();
      } else if (activeTab === 'notifications') {
        await fetchNotifications();
      }
      await fetchNotifications();
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialReport = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/financial-report?period=${reportPeriod}`, { headers: authHeaders() });
      if (r.ok) setFinancialReport(await r.json());
    } catch (e) { console.error('Error fetching financial report:', e); }
  };

  const fetchRevenueChart = async () => {
    try {
      const days = reportPeriod === '7d' ? 7 : reportPeriod === '30d' ? 30 : 90;
      const r = await fetch(`${BACKEND_URL}/api/super-admin/revenue-chart?days=${days}`, { headers: authHeaders() });
      if (r.ok) setRevenueChartData((await r.json()).chart_data || []);
    } catch (e) { console.error('Error fetching revenue chart:', e); }
  };

  const fetchAgents = async () => {
    try {
      const r1 = await fetch(`${BACKEND_URL}/api/super-admin/all-users?role=agent`, { headers: authHeaders() });
      if (r1.ok) setAgents((await r1.json()).users || []);
      const r2 = await fetch(`${BACKEND_URL}/api/super-admin/agent-balances`, { headers: authHeaders() });
      if (r2.ok) {
        const d = await r2.json();
        setAgentBalances(d.agents || []);
        setAgentSummary(d.summary);
      }
      const r3 = await fetch(`${BACKEND_URL}/api/super-admin/agent-commissions?status=pending`, { headers: authHeaders() });
      if (r3.ok) setAgentCommissions((await r3.json()).commissions || []);
    } catch (e) { console.error('Error fetching agents:', e); }
  };

  const fetchNotifications = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/notifications?limit=100`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setNotifications(d.notifications || []);
        setUnreadCount(d.unread_count || 0);
      }
    } catch (e) { console.error('Error fetching notifications:', e); }
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/notifications/${id}/read`, { method: 'POST', headers: authHeaders() });
      if (r.ok) fetchNotifications();
    } catch (e) { console.error('Error marking notification read:', e); }
  };

  const handleMarkAllRead = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/notifications/mark-all-read`, { method: 'POST', headers: authHeaders() });
      if (r.ok) { toast.success('All notifications marked as read'); fetchNotifications(); }
    } catch (e) { console.error('Error marking all read:', e); }
  };

  const handleDeleteNotification = async (id) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/notifications/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (r.ok) { toast.success('Notification deleted'); setSelectedNotification(null); fetchNotifications(); }
    } catch (e) { console.error('Error deleting notification:', e); }
  };

  const downloadReport = async (format) => {
    setExportingReport(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/financial-report/export/${format}?period=${reportPeriod}`, { headers: authHeaders() });
      if (r.ok) {
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `financial_report_${reportPeriod}_${new Date().toISOString().slice(0, 10)}.${format}`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`${format.toUpperCase()} report downloaded successfully!`);
      } else {
        toast.error(`Failed to export ${format.toUpperCase()}`);
      }
    } catch (e) {
      console.error(`Error exporting ${format}:`, e);
      toast.error(`Error exporting ${format.toUpperCase()} report`);
    } finally {
      setExportingReport(false);
    }
  };

  const handleExportCSV = () => downloadReport('csv');
  const handleExportPDF = () => downloadReport('pdf');

  const handleExportInquiriesCSV = async () => {
    setExportingInquiries(true);
    try {
      const qs = peptideOnly ? '?peptide_only=true' : '';
      const r = await fetch(`${BACKEND_URL}/api/super-admin/inquiries/export/csv${qs}`, { headers: authHeaders() });
      if (r.ok) {
        const blob = await r.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const prefix = peptideOnly ? 'peptide_leads' : 'inquiries';
        a.download = `${prefix}_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(`${peptideOnly ? 'Peptide leads' : 'Inquiries'} CSV downloaded`);
      } else {
        toast.error('Failed to export inquiries CSV');
      }
    } catch (e) {
      console.error('Error exporting inquiries CSV:', e);
      toast.error('Error exporting inquiries CSV');
    } finally {
      setExportingInquiries(false);
    }
  };

  const handleApproveCommissions = async (agentId) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/agent-commissions/approve?agent_id=${agentId}`, { method: 'POST', headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Approved ${d.approved_count} commissions`);
        fetchAgents();
      } else {
        const e = await r.json();
        toast.error(e.detail || 'Failed to approve commissions');
      }
    } catch (e) {
      console.error('Error approving commissions:', e);
      toast.error('Error approving commissions');
    }
  };

  const openAgentPayoutModal = (agent) => {
    setSelectedAgent(agent);
    setAgentPayoutAmount(agent.approved_balance?.toString() || '');
    setAgentPayoutReference(''); setAgentPayoutNotes('');
    setShowAgentPayoutModal(true);
  };

  const handleAgentPayout = async () => {
    if (!selectedAgent) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/agent-commissions/payout`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: selectedAgent.agent_id,
          amount: parseFloat(agentPayoutAmount) || null,
          payment_method: 'manual',
          payment_reference: agentPayoutReference || null,
          notes: agentPayoutNotes || null
        })
      });
      if (r.ok) {
        toast.success((await r.json()).message);
        setShowAgentPayoutModal(false); setSelectedAgent(null);
        fetchAgents();
      } else {
        toast.error((await r.json()).detail || 'Failed to create payout');
      }
    } catch (e) {
      console.error('Error creating agent payout:', e);
      toast.error('Error creating payout');
    }
  };

  const handleInquiryStatusChange = async (id, newStatus) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/inquiry/${id}/status?status=${newStatus}`, { method: 'PUT', headers: authHeaders() });
      if (r.ok) fetchData();
    } catch (e) { console.error('Error updating inquiry status:', e); }
  };

  const triggerSubscriptionCheck = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/trigger-subscription-check`, { method: 'POST', headers: authHeaders() });
      if (r.ok) alert('Subscription check triggered successfully!');
    } catch (e) { console.error('Error triggering subscription check:', e); }
  };

  const fetchBankAccount = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/bank-account`, { headers: authHeaders() });
      if (r.ok) setBankAccount(await r.json());
    } catch (e) { console.error('Error fetching bank account:', e); }
  };

  const fetchPendingVerifications = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/pending-bank-verifications`, { headers: authHeaders() });
      if (r.ok) setPendingVerifications((await r.json()).pending_verifications || []);
    } catch (e) { console.error('Error fetching pending verifications:', e); }
  };

  const fetchMerchantFees = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/merchant-fees`, { headers: authHeaders() });
      if (r.ok) setMerchantFees((await r.json()).merchants || []);
    } catch (e) { console.error('Error fetching merchant fees:', e); }
  };

  const fetchMerchantBalances = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/merchant-balances`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setMerchantBalances(d.balances || []);
        setBalanceSummary(d.summary || null);
      }
    } catch (e) { console.error('Error fetching merchant balances:', e); }
  };

  const fetchPayouts = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/payouts?limit=50`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setPayouts(d.payouts || []);
        setPayoutsSummary(d.summary || null);
      }
    } catch (e) { console.error('Error fetching payouts:', e); }
  };

  const handleCreatePayout = async () => {
    if (!selectedMerchant) return;
    try {
      const body = { merchant_id: selectedMerchant.merchant_id, notes: payoutNotes || null };
      if (payoutAmount && parseFloat(payoutAmount) > 0) body.amount = parseFloat(payoutAmount);
      const r = await fetch(`${BACKEND_URL}/api/super-admin/payouts/create`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Payout of $${d.payout.amount.toFixed(2)} created successfully!`);
        setShowPayoutModal(false); setSelectedMerchant(null);
        setPayoutAmount(''); setPayoutNotes('');
        fetchMerchantBalances(); fetchPayouts();
      } else {
        toast.error(`Failed: ${(await r.json()).detail || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Error creating payout:', e);
      toast.error('Failed to create payout');
    }
  };

  const handleUpdatePayout = async () => {
    if (!selectedPayout || !payoutUpdateStatus) return;
    try {
      const body = { status: payoutUpdateStatus, admin_notes: payoutAdminNotes || null };
      if (payoutUpdateStatus === 'failed' && payoutFailureReason) body.failure_reason = payoutFailureReason;
      const r = await fetch(`${BACKEND_URL}/api/super-admin/payouts/${selectedPayout.id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (r.ok) {
        toast.success(`Payout status updated to ${payoutUpdateStatus}!`);
        setShowUpdatePayoutModal(false); setSelectedPayout(null);
        setPayoutUpdateStatus(''); setPayoutAdminNotes(''); setPayoutFailureReason('');
        fetchMerchantBalances(); fetchPayouts();
      } else {
        toast.error(`Failed: ${(await r.json()).detail || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Error updating payout:', e);
      toast.error('Failed to update payout');
    }
  };

  const handleRecalculateBalances = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/recalculate-balances`, { method: 'POST', headers: authHeaders() });
      if (r.ok) {
        toast.success((await r.json()).message);
        fetchMerchantBalances();
      } else {
        toast.error('Failed to recalculate balances');
      }
    } catch (e) {
      console.error('Error recalculating balances:', e);
      toast.error('Failed to recalculate balances');
    }
  };

  const openPayoutModal = (m) => {
    setSelectedMerchant(m);
    setPayoutAmount(m.pending_balance?.toFixed(2) || '0');
    setPayoutNotes('');
    setShowPayoutModal(true);
  };

  const openUpdatePayoutModal = (p) => {
    setSelectedPayout(p);
    setPayoutUpdateStatus('');
    setPayoutAdminNotes(p.admin_notes || '');
    setPayoutFailureReason('');
    setShowUpdatePayoutModal(true);
  };

  const getPayoutStatusBadge = (status) => {
    const colors = {
      pending: '#f59e0b', processing: '#3b82f6',
      completed: '#22c55e', failed: '#ef4444', cancelled: '#6b7280'
    };
    return (
      <span className="status-badge" style={{ color: colors[status] || '#6b7280', borderColor: colors[status] || '#6b7280' }}>
        {status === 'pending' && <Clock size={12} />}
        {status === 'processing' && <RefreshCw size={12} />}
        {status === 'completed' && <CheckCircle size={12} />}
        {status === 'failed' && <XCircle size={12} />}
        {status === 'cancelled' && <X size={12} />}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleBankVerification = async (userId, action) => {
    try {
      const url = new URL(`${BACKEND_URL}/api/super-admin/verify-bank/${userId}`);
      url.searchParams.append('action', action);
      if (action === 'reject' && rejectionReason) url.searchParams.append('rejection_reason', rejectionReason);
      const r = await fetch(url, { method: 'POST', headers: authHeaders() });
      if (r.ok) {
        alert(`Bank account ${action === 'approve' ? 'approved' : 'rejected'} successfully!`);
        setShowRejectModal(false); setSelectedUser(null); setRejectionReason('');
        fetchPendingVerifications();
      } else {
        alert(`Failed: ${(await r.json()).detail || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Error verifying bank:', e);
      alert('Failed to process verification');
    }
  };

  const handleUpdateFee = async (userId) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/merchant-fees/${userId}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ platform_fee_percentage: parseFloat(newFeeValue) })
      });
      if (r.ok) {
        alert('Fee updated successfully!');
        setEditingFee(null); setNewFeeValue('');
        fetchMerchantFees();
      } else {
        alert(`Failed: ${(await r.json()).detail || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Error updating fee:', e);
      alert('Failed to update fee');
    }
  };

  const saveBankAccount = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/bank-account`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(bankDetails)
      });
      if (r.ok) {
        alert('Bank account saved successfully!');
        setShowBankModal(false);
        fetchBankAccount();
      }
    } catch (e) {
      console.error('Error saving bank account:', e);
      alert('Failed to save bank account');
    }
  };

  const sendAgreement = async (userEmail) => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/send-agreement`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail })
      });
      const d = await r.json();
      if (r.ok) {
        alert(`✅ Agreement sent to ${userEmail}\n\nThe user will receive an email with instructions to sign the agreement.`);
      } else {
        alert(`❌ Failed to send agreement: ${d.detail || 'Unknown error'}`);
      }
    } catch (e) {
      console.error('Error sending agreement:', e);
      alert(`❌ Error sending agreement: ${e.message}`);
    }
  };

  const filteredUsers = allUsers.filter(u => {
    const matchesSearch = u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          u.business_name.toLowerCase().includes(searchTerm.toLowerCase());
    if (statusFilter === 'all') return matchesSearch;
    if (statusFilter === 'owners') return matchesSearch && u.role === 'owner';
    if (statusFilter === 'agents') return matchesSearch && u.is_agent;
    if (statusFilter === 'cashiers') return matchesSearch && u.role === 'cashier';
    return matchesSearch;
  });

  const filteredInquiries = allInquiries.filter(i => {
    const matchesSearch = i.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          i.email.toLowerCase().includes(searchTerm.toLowerCase());
    if (statusFilter === 'all') return matchesSearch;
    return matchesSearch && i.status === statusFilter;
  });

  return {
    navigate,
    activeTab, setActiveTab,
    stats, allUsers, allTransactions, allInquiries, schedulerStatus,
    loading, searchTerm, setSearchTerm, statusFilter, setStatusFilter,
    peptideOnly, setPeptideOnly, exportingInquiries, handleExportInquiriesCSV,
    landingConversion,
    bankAccount, setBankAccount, showBankModal, setShowBankModal,
    bankDetails, setBankDetails,
    notifications, setNotifications, unreadCount, setUnreadCount,
    selectedNotification, setSelectedNotification,
    pendingVerifications, setPendingVerifications,
    showRejectModal, setShowRejectModal,
    selectedUser, setSelectedUser,
    rejectionReason, setRejectionReason,
    merchantFees, editingFee, setEditingFee,
    newFeeValue, setNewFeeValue,
    merchantBalances, balanceSummary, payouts, payoutsSummary,
    showPayoutModal, setShowPayoutModal,
    selectedMerchant, setSelectedMerchant,
    payoutAmount, setPayoutAmount, payoutNotes, setPayoutNotes,
    showUpdatePayoutModal, setShowUpdatePayoutModal,
    selectedPayout, setSelectedPayout,
    payoutUpdateStatus, setPayoutUpdateStatus,
    payoutAdminNotes, setPayoutAdminNotes,
    payoutFailureReason, setPayoutFailureReason,
    financialReport, revenueChartData, reportPeriod, setReportPeriod,
    exportingReport,
    agents, agentBalances, agentCommissions, agentSummary,
    showAgentPayoutModal, setShowAgentPayoutModal,
    selectedAgent, setSelectedAgent,
    agentPayoutAmount, setAgentPayoutAmount,
    agentPayoutReference, setAgentPayoutReference,
    agentPayoutNotes, setAgentPayoutNotes,
    fetchData, fetchFinancialReport, fetchRevenueChart, fetchAgents,
    fetchNotifications, handleMarkNotificationRead, handleMarkAllRead,
    handleDeleteNotification, handleExportCSV, handleExportPDF,
    handleApproveCommissions, openAgentPayoutModal, handleAgentPayout,
    handleInquiryStatusChange, triggerSubscriptionCheck, fetchBankAccount,
    fetchPendingVerifications, fetchMerchantFees, fetchMerchantBalances,
    fetchPayouts, handleCreatePayout, handleUpdatePayout,
    handleRecalculateBalances, openPayoutModal, openUpdatePayoutModal,
    getPayoutStatusBadge, handleBankVerification, handleUpdateFee,
    saveBankAccount, sendAgreement,
    filteredUsers, filteredInquiries,
  };
}
